import sortBy from 'lodash/sortBy';
import { calculateExchangePrice } from '../../actions/exchange-actions';
import { ActionTypes } from '../../actions/index';
import { CartSeat, Seat, SeatPriceQuantity, SeatStyle, SeatStyleCulture, SeatZone } from '../../classes/seat';
import { ZoneUpsell } from '../../classes/zone-upsell';
import isMobile from '../../helpers/is-mobile';


interface PriceTypeQuantity {
    priceTypeId: number;
    quantity: number;
}

export interface ISeatMapModel {
    brandId: string;
    levelId: string;
    seatmapId: string;
    levelSeats: Array<Seat>;
    levelBackgroundPaths: Array<string>;
    allSeatPricing: Array<SeatZone>
    levelSvgBackgroundImage: string;
    selectedSeats: Array<Seat>;
    seatStyles: Array<SeatStyle>;
    seatStyleCultures: Array<SeatStyleCulture>;
    currentZoom: number;
    zoomChanged: boolean;
    openSeatmapFilters: boolean;
    openSeatmapCart: boolean;
    openSeatingInfo: boolean;
    seatHoverX: number;
    seatHoverY: number;
    seatsHovered: Array<Seat>;
    zoneUpsells: Array<ZoneUpsell>;
    selectedPriceQuantities: Array<SeatPriceQuantity>;
    seatUpsoldCount: number;
    hasSeatViewImages: boolean;

    mobileSeatMapViewBox: string;
    tabletSeatMapViewBox: string;
    confirmedAttestation: Array<string>;
    loadedExchangePricing: boolean;
}

let defaultSeatMapModel: ISeatMapModel = {
    brandId: '',
    levelId: '',
    seatmapId: '',
    levelSeats: [],
    levelBackgroundPaths: [],
    levelSvgBackgroundImage: '',
    selectedSeats: [],
    //activeSeat: { seatId: '', svgPath: '', tessituraSeat: null, seatStyleId: '', seatStyle: null },
    allSeatPricing: [],
    seatStyles: [],
    seatStyleCultures: [],
    currentZoom: null,
    zoomChanged: false,
    openSeatmapFilters: true,
    openSeatmapCart: false,
    openSeatingInfo: false,
    seatHoverX: null,
    seatHoverY: null,
    seatsHovered: [],
    zoneUpsells: [],
    selectedPriceQuantities: [],
    seatUpsoldCount: 0,
    confirmedAttestation: [],
    mobileSeatMapViewBox: "",
    tabletSeatMapViewBox: "",
    hasSeatViewImages: false,
    loadedExchangePricing: false
};

export { defaultSeatMapModel };

export function seatMapModel(state: ISeatMapModel = defaultSeatMapModel, action) {
    switch (action.type) {
        case ActionTypes.RECEIVE_SETTINGS:
            return { ...state, openSeatmapFilters: isMobile() ? false : action.value.expandFiltersOnSeatmapLoad };
        case ActionTypes.CLOSE_SEATMAP_FILTERS:
            return { ...state, openSeatmapFilters: false };
        case ActionTypes.OPEN_SEATMAP_FILTERS:
            return { ...state, openSeatmapFilters: true, openSeatmapCart: false, openSeatingInfo: false };
        case ActionTypes.CLOSE_SEATMAP_CART:
            return { ...state, openSeatmapCart: false };
        case ActionTypes.OPEN_SEATMAP_CART:
            return {
                ...state, openSeatmapCart: true, openSeatmapFilters: false, openSeatingInfo: isMobile() ? false : state.openSeatingInfo
            };
        case ActionTypes.CLOSE_SEATING_INFO:
            return { ...state, openSeatingInfo: false };
        case ActionTypes.OPEN_SEATING_INFO:
            return { ...state, openSeatingInfo: true, openSeatmapFilters: false };
        case ActionTypes.ADD_SEAT_TO_CART:
            let newSeat = action.value as CartSeat;
            let newAttested = [];
            if (newSeat.seatStyleCulture.confirmPrompt != null && newSeat.seatStyleCulture.confirmPrompt.length > 0 && state.confirmedAttestation.indexOf(newSeat.seatStyleCulture.confirmPrompt) < 0) {
                newAttested = newAttested.concat(newSeat.seatStyleCulture.confirmPrompt);
            }

            return { ...state, openSeatmapFilters: false, confirmedAttestation: state.confirmedAttestation.concat(newAttested) };
        case ActionTypes.LOAD_SEATMAP_BACKGROUND:
            return { ...state, levelBackgroundPaths: action.value.response };
        case ActionTypes.SET_CURRENT_EXCHANGE_TICKETS:
            let newExchangePricing = state.allSeatPricing.map(pricing => {
                pricing.prices = pricing.prices.map(pr => {
                    pr.price = calculateExchangePrice(pr.defaultPrice, action.value.exchangeTickets);
                    return pr;
                });
                return pricing;
            });

            const cartSeats = action.value.cartSeats as Array<CartSeat>;

            const newLevelSeats = state.levelSeats.map(seat => {
                let seatPrice = newExchangePricing.filter(pricing => pricing.zoneId === seat.tessituraSeat.zoneId)[0];
                if (seatPrice !== undefined && seat.tessituraSeat.isAvailable && !cartSeats.some(cartS => cartS.seatId === seat.seatId)) {

                    //exchanges should only have one price type
                    if (seatPrice.prices[0].price > 0) {
                        seat.seatStyle = state.seatStyles.filter(style => {
                            return style.id === seat.exchangeHigherPriceSeatStyleId;
                        })[0];

                        let styleCulture = state.seatStyleCultures.filter(style => {
                            return style.seatStyleId === seat.exchangeHigherPriceSeatStyleId;
                        })[0];

                        seat.seatStyleCulture = styleCulture;

                        if (seat.seatStyle === undefined) {
                            console.log("missing seat style:" + seat.seatStyleId + " for seat", seat);
                            let unavailable = newState.seatStyles.filter(style => style.name.toLocaleLowerCase() === "unavailable");
                            seat.tessituraSeat.isAvailable = false;

                            if (unavailable.length > 0) {
                                seat.seatStyleId = unavailable[0].id;
                                seat.seatStyle = unavailable[0];
                            } else {
                                console.log("BUT NO UNAVAIBLE STYLE:", newState.seatStyles);
                            }
                        }

                        if (seat.exchangeHigherPriceSvgPath != null) {
                            seat.activeSvgPath = seat.exchangeHigherPriceSvgPath;
                        }
                    } else {
                        seat.seatStyle = state.seatStyles.filter(style => {
                            return style.id === seat.exchangeSamePriceSeatStyleId;
                        })[0];

                        let styleCulture = state.seatStyleCultures.filter(style => {
                            return style.seatStyleId === seat.exchangeSamePriceSeatStyleId;
                        })[0];

                        seat.seatStyleCulture = styleCulture;

                        if (seat.seatStyle === undefined) {
                            console.log("missing seat style:" + seat.seatStyleId + " for seat", seat);
                            let unavailable = newState.seatStyles.filter(style => style.name.toLocaleLowerCase() === "unavailable");
                            seat.tessituraSeat.isAvailable = false;

                            if (unavailable.length > 0) {
                                seat.seatStyleId = unavailable[0].id;
                                seat.seatStyle = unavailable[0];
                            } else {
                                console.log("BUT NO UNAVAIBLE STYLE:", newState.seatStyles);
                            }
                        }

                        if (seat.exchangeHigherPriceSvgPath != null) {
                            seat.activeSvgPath = seat.exchangeSamePriceSvgPath;
                        }
                    }

                }
                return seat;
            });
            return { ...state, allSeatPricing: newExchangePricing, levelSeats: newLevelSeats, loadedExchangePricing: true };
        case ActionTypes.REQUEST_SEATMAP:
            return { ...state, loadedExchangePricing: false };
        case ActionTypes.LOAD_SEATMAP:
            const newState = action.value.response as ISeatMapModel;

            const selectedPriceTypeQuantities = action.value.selectedPriceTypes as Array<PriceTypeQuantity>;
            const unavailableStyles = newState.seatStyles.filter(style => style.name.toLocaleLowerCase() === "unavailable");
            const levelPricing = newState.allSeatPricing;

            // Assign the appropriate seat style and view to each seat and set availability.
            const levelSeats = newState.levelSeats.map(seat => {
                const zonePrice = levelPricing.filter(pricing => pricing.zoneId === seat.tessituraSeat.zoneId);
                const selectedPriceTypeIds = selectedPriceTypeQuantities.map(priceTypeQuantity => priceTypeQuantity.priceTypeId)

                if ((zonePrice.length == 0 || zonePrice.every(pr => pr.prices.length == 0)) && seat.tessituraSeat.isAvailable) {
                    console.log("SEAT IS AVAILABLE BUT MISSING ZONE:", seat.tessituraSeat);
                    if (unavailableStyles.length > 0) {
                        seat.seatStyleId = unavailableStyles[0].id;
                        seat.tessituraSeat.isAvailable = false;
                    }
                }
                else if (zonePrice.length > 0 && selectedPriceTypeIds.length > 0) {
                    //const selectedPrice = zonePrice[0].prices.filter(price => selectedPriceTypeIds.indexOf(+price.priceTypeId) >= 0);

                    if (seat.seatingOptionGroup != null
                        && seat.seatingOptionGroup.unlockSeatingOptionGroupId !== null
                        && seat.seatingOptionGroup.unlockSeatingOptionGroupId.length > 0) {
                        seat.tessituraSeat.isDisabled = true;
                    }

                }

                const matchedCartStyles = state.seatStyles.filter(style => {
                    return style.id === seat.inCartSeatStyleId;
                });

                if (action.value.cartSeats.length > 0
                    && action.value.cartSeats.some(currentCartSeat => currentCartSeat.seatId === seat.seatId)
                    && matchedCartStyles.length > 0) {

                    seat.seatStyleCulture = state.seatStyleCultures.filter(style => {
                        return style.seatStyleId === seat.inCartSeatStyleId;
                    })[0];

                    seat.seatStyle = state.seatStyles.filter(style => {
                        return style.id === seat.inCartSeatStyleId;
                    })[0];

                    if (!seat.seatStyle) {
                        console.log("missing cart seat style:" + seat.seatStyleId + " for seat", seat);
                        if (unavailableStyles.length > 0) {
                            seat.seatStyleId = unavailableStyles[0].id;
                            seat.seatStyle = unavailableStyles[0];
                            seat.tessituraSeat.isAvailable = false;
                        }
                    }

                    seat.activeSvgPath = seat.inCartSeatPath;
                    //seat.svgPath = seat.inCartSeatPath;;

                } else {

                    seat.seatStyle = newState.seatStyles.filter(style => {
                        return style.id === seat.seatStyleId;
                    })[0];

                    let styleCulture = newState.seatStyleCultures.filter(style => {
                        return style.seatStyleId === seat.seatStyleId;
                    })[0];

                    seat.seatStyleCulture = styleCulture;

                    seat.originalSeatStyleCulture = styleCulture;

                    if (seat.seatStyle === undefined) {
                        console.log("missing seat style:" + seat.seatStyleId + " for seat", seat);

                        if (unavailableStyles.length > 0) {
                            seat.seatStyleId = unavailableStyles[0].id;
                            seat.seatStyle = unavailableStyles[0];
                            seat.tessituraSeat.isAvailable = false;
                        }
                    }

                    seat.activeSvgPath = seat.svgPath;

                }

                return seat;
            });

            const levelBackgroundPaths = [];
            return {
                ...state,
                ...action.value.response,
                levelSeats,
                levelBackgroundPaths
            };
        case ActionTypes.LOAD_SEAT_INCART_STYLE:
            var cartSeat = action.value as Seat;
            return {
                ...state,
                // For each seat
                levelSeats: state.levelSeats.map(seat => {
                    // give it the appropriate style object
                    if (seat.seatId === cartSeat.seatId) {
                        seat.activeSvgPath = seat.inCartSeatPath;

                        seat.seatStyleCulture = state.seatStyleCultures.filter(style => {
                            return style.seatStyleId === seat.inCartSeatStyleId;
                        })[0];

                        seat.seatStyle = state.seatStyles.filter(style => {
                            return style.id === seat.inCartSeatStyleId;
                        })[0];

                        if (seat.seatStyle == null) {
                            console.log("missing cart seat style:" + seat.seatStyleId);
                        }
                    }
                    return seat;
                })
            };
        case ActionTypes.REMOVE_SEAT_INCART_STYLE:
            var cartSeat = action.value as Seat;
            return {
                ...state,
                // For each seat
                levelSeats: state.levelSeats.map(seat => {
                    // give it the appropriate style object
                    if (seat.seatId === cartSeat.seatId) {
                        seat.seatStyle = state.seatStyles.filter(style => {
                            return style.id === seat.seatStyleId;
                        })[0];

                        if (seat.seatStyle == null) {
                            console.log("missing cart seat style:" + seat.seatStyleId);
                        }

                        seat.seatStyleCulture = state.seatStyleCultures.filter(style => {
                            return style.seatStyleId === seat.seatStyleId;
                        })[0];

                        seat.activeSvgPath = seat.svgPath;
                    }
                    return seat;
                })
            };

        // Add seat to selected and close the cart
        case ActionTypes.SELECT_SEAT:
            const actionValue = action.value as { seats: Seat[], openInfo: boolean };
            const selectedSeats = actionValue.seats.map(seat => {
                seat.associatedSeatmapId = seat.associatedSeatmapId || state.seatmapId;
                return seat;
            });
            return {
                ...state,
                selectedSeats: selectedSeats.sort((a, b) => (+b.tessituraSeat.isAvailable) - (+a.tessituraSeat.isAvailable)),
                openSeatmapCart: !isMobile() ? false : state.openSeatmapCart,
                openSeatingInfo: actionValue.openInfo,
                openSeatmapFilters: false
            };

        case ActionTypes.UNSELECT_SEAT:
            const emptySeats = [];
            return { ...state, selectedSeats: emptySeats };
        case ActionTypes.UNLOCK_SEATS:
            let unlockableSeats = action.value as Array<Seat>;

            let regularSeats = state.levelSeats.filter(seat => !unlockableSeats.some(s => s.seatId === seat.seatId));

            return {
                ...state,
                levelSeats: sortBy(regularSeats.concat(unlockableSeats.map(unlockSeat => {
                    unlockSeat.tessituraSeat.isDisabled = false;
                    return unlockSeat;
                })), s => s.seatId),
                cartUpdated: true
            };
        case ActionTypes.LOCK_SEATS:
            let lockableSeats = action.value as Array<Seat>;

            let unlockedSeats = state.levelSeats.filter(seat => !lockableSeats.some(s => s.seatId === seat.seatId));

            return {
                ...state,
                levelSeats: sortBy(unlockedSeats.concat(lockableSeats.map(lockSeat => {
                    lockSeat.tessituraSeat.isDisabled = true;
                    return lockSeat;
                })), s => s.seatId),
                cartUpdated: true
            };
        case ActionTypes.LOCK_ALL_SEATS:
            return {
                ...state,
                levelSeats: state.levelSeats.map(lockSeat => {
                    lockSeat.tessituraSeat.isDisabled = true;
                    return lockSeat;
                }),
                cartUpdated: true
            };
        case ActionTypes.ON_ZOOM:
            return { ...state, currentZoom: action.value.newZoom, zoomChanged: action.value.zoomChanged };
        case ActionTypes.SELECT_LEVEL:
            return { ...state, seatmapId: action.value, loaded: false };
        case ActionTypes.HOVER_SEAT:
            return { ...state, seatHoverX: action.value.xCoord, seatHoverY: action.value.yCoord, seatsHovered: action.value.seatsHovered };
        case ActionTypes.SET_SELECTED_PRICE_QUANTITIES:
            return {
                ...state, selectedPriceQuantities: action.value
            }
        case ActionTypes.SELECT_ZONE_UPSELL:
            return {
                ...state,
                seatUpsoldCount: state.seatUpsoldCount + 1
            }
        case ActionTypes.RESET_ZONE_UPSELL:
            return {
                ...state,
                seatUpsoldCount: 0
            }
        default:
            return state;
    }
};
